import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Switch,
  MenuItem,
  FormControlLabel,
  FormControl,
  InputLabel,
  FormHelperText,
  Stack,
  Grid,
  InputAdornment,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Select
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const CATEGORIES = {
  harmony: { label: 'Harmony', color: 'primary' },
  prosperity: { label: 'Prosperity', color: 'success' },
  love: { label: 'Love', color: 'secondary' },
  healing: { label: 'Healing', color: 'info' },
  creativity: { label: 'Creativity', color: 'warning' },
  protection: { label: 'Protection', color: 'error' },
};

export default function ProductForm({ product, onSubmit, onCancel, isLoading }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    image_url: '',
    stock: '',
    is_active: true,
    category: '',
    energy_properties: '',
    flowers: '',
    ...product,
    // Convert flowers array to comma-separated string if it's an array
    flowers: Array.isArray(product?.flowers) ? product.flowers.join(', ') : (product?.flowers || '')
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('forms.required');
    }
    
    if (!formData.description.trim()) {
      newErrors.description = t('forms.required');
    }
    
    if (!formData.price || formData.price <= 0) {
      newErrors.price = t('forms.invalidPrice');
    }
    
    if (!formData.image_url.trim()) {
      newErrors.image_url = t('forms.required');
    }
    
    if (!formData.category) {
      newErrors.category = t('forms.required');
    }
    
    if (!formData.energy_properties.trim()) {
      newErrors.energy_properties = t('forms.required');
    }
    
    if (!formData.flowers.trim()) {
      newErrors.flowers = t('forms.required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const submissionData = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock) || 0,
        flowers: formData.flowers.split(',').map(f => f.trim()).filter(f => f),
      };
      onSubmit(submissionData);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle>
        {product ? t('products.edit') : t('products.create')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label={t('products.name')}
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            disabled={isLoading}
          />

          <TextField
            fullWidth
            label={t('products.description')}
            name="description"
            value={formData.description}
            onChange={handleChange}
            error={!!errors.description}
            helperText={errors.description}
            multiline
            rows={4}
            disabled={isLoading}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('products.price')}
                name="price"
                type="number"
                value={formData.price}
                onChange={handleChange}
                error={!!errors.price}
                helperText={errors.price}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('products.stock')}
                name="stock"
                type="number"
                value={formData.stock}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            label={t('products.imageUrl')}
            name="image_url"
            value={formData.image_url}
            onChange={handleChange}
            error={!!errors.image_url}
            helperText={errors.image_url}
            disabled={isLoading}
          />

          <FormControl fullWidth error={!!errors.category}>
            <InputLabel>{t('products.category')}</InputLabel>
            <Select
              name="category"
              value={formData.category}
              onChange={handleChange}
              label={t('products.category')}
              disabled={isLoading}
            >
              {Object.entries(CATEGORIES).map(([value, { label }]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
          </FormControl>

          <TextField
            fullWidth
            label={t('products.energyProperties')}
            name="energy_properties"
            value={formData.energy_properties}
            onChange={handleChange}
            error={!!errors.energy_properties}
            helperText={errors.energy_properties}
            multiline
            rows={2}
            disabled={isLoading}
          />

          <TextField
            fullWidth
            label={t('products.flowers')}
            name="flowers"
            value={formData.flowers}
            onChange={handleChange}
            error={!!errors.flowers}
            helperText={errors.flowers || t('products.flowersHelp')}
            disabled={isLoading}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={handleChange}
                name="is_active"
                disabled={isLoading}
              />
            }
            label={t('products.active')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button 
          type="submit" 
          variant="contained" 
          disabled={isLoading}
          endIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </form>
  );
}
